import { useState, useEffect, useRef, useMemo } from "react";
import { initializeApp } from "firebase/app";
import { Database } from 'firebase/database';
import { getFirestore, doc, getDoc, updateDoc, arrayUnion, arrayRemove, onSnapshot, setDoc, deleteDoc, runTransaction } from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword, onAuthStateChanged, GoogleAuthProvider,signInWithEmailAndPassword, signOut, 
         deleteUser, reauthenticateWithPopup, EmailAuthProvider, reauthenticateWithCredential, sendEmailVerification, User } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getDatabase, ref as databaseRef, onValue, remove } from "firebase/database";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Inizializza Firebase
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const realtimeDb = getDatabase(app);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
const storage = getStorage(app);



// --- Tipi di Dati ---
type Cliente = {
  Denominazione: string;
  partita_iva: string;
  indirizzo: string;
  cap: string;
  comune: string;
  nazione: string;
};

export interface DatiAnagraficaAzienda {
  ragione_sociale: string;
  partita_iva: string;
  indirizzo: string;
  cap: string;
  comune: string;
  nazione: string;
}

interface StatisticheUtente {
  fileCaricati: number;
  tempoUtilizzoSecondi: number;
  ultimoAccessoTimestamp: string;
}

interface InvitiEmessi{
  [emailPlaceholder: string]: {
    email: string;
    tipo: string;
    stato: string;
  };
}

export interface FileCliente {
  nome: string ;
  stato: 'Inviato' | 'Scaricato' | 'Elaborazione in corso' | 'Elaborato' ;
  timestampCaricamento: number ;
  xmlElaboratiDa: string ;
}

export interface FileClienteArricchito extends Omit<FileCliente, 'xmlElaboratiDa'> {
  xmlElaboratiDa: {
    uid: string;
    email: string;
    userPhotoURL: string;
  } | string;
}

export interface OptionsState {
  originalNumero_fattura : boolean;
  originalIndirizzo_P: boolean;
  originalCAP_P: boolean;
  originalComune_P: boolean;
  originalIdPaese_T: boolean;
  originalIdCodice_T: boolean;
  originalIdPaese_P: boolean;
  originalIdCodice_P: boolean;
  //originalIdPaese_C: boolean;
}

export const initialOptions: OptionsState = {
    originalNumero_fattura: false,
    originalIndirizzo_P: false,
    originalCAP_P: false, 
    originalComune_P: false,
    originalIdPaese_T: false,
    originalIdCodice_T: false,
    originalIdPaese_P: false,
    originalIdCodice_P: false,
  };

// Interfaccia base per tutti gli utenti nel DB
export interface UtenteDB {
  uid: string;
  anagrafica_clienti:Cliente[];
  email: string;
  userPhotoURL: string;
  data_registrazione: number;
  dati_utente_completi: boolean; 
  error_log: string[];
  statistiche_utente: StatisticheUtente;
  invitatoDaUID?: string; 
  statoInvito?: 'Registrato' | 'in attesa';
  storico_file: Record<string, number>[];
  tipo_account: 'proprietario' | 'azienda' | 'collaboratore' | 'cliente' | string;
  aziende_invitate:string[];
  collaboratori_invitati:string[];
  clienti_invitati:string[];
  file_cliente: FileCliente[];
  inviti_emessi: InvitiEmessi;
  avviso_setting: boolean;
  OptionsState: OptionsState;
}

interface CategorizedFiles {
  Inviato: FileClienteArricchito[];
  Elaborato: FileClienteArricchito[];
}

export interface FirestoreData extends UtenteDB, Partial<DatiAnagraficaAzienda> {
  aziende_invitate: string[];
  collaboratori_invitati: string[];
  clienti_invitati: string[];
}


//mappa errori
const getFriendlyFirebaseError = (errorCode: string): string => {
  const errors: { [key: string]: string } = {
    "auth/invalid-email": "L'indirizzo email non è valido.",
    "auth/user-disabled": "Questo account è stato disabilitato.",
    "auth/user-not-found": "Utente non trovato. Controlla l'email inserita.",
    "auth/wrong-password": "Password errata. Riprova.",
    "auth/email-already-in-use": "L'email è già in uso da un altro account.",
    "auth/weak-password": "La password è troppo debole. Usa almeno 6 caratteri.",
    "auth/too-many-requests": "Troppi tentativi. Attendi e riprova più tardi.",
    "auth/network-request-failed": "Errore di rete. Verifica la connessione.",
  };

  return errors[errorCode] ||"Controlla le credenziali e riprova!";
};


export async function inviaEmailVerifica(user: User): Promise<void> {
    try {
        await sendEmailVerification(user);
    } catch (error: any) {
        console.error("Errore nell'invio dell'email di verifica:", error);
        throw new Error("Errore durante l'invio dell'email di verifica. Riprova più tardi.");
    }
}


//crea il documento utente nel data base
export const CreaDocUt = async (uid: string, email: string | null , photoURL: string ): Promise<boolean> => {
  try{
    const docRef = doc(db, "utenti", uid);

    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return false;
    }

    const initialData: Partial<UtenteDB> = { 
      anagrafica_clienti: [],
      userPhotoURL: String(photoURL || ''),
      email: String(email),
      tipo_account: '', 
      data_registrazione: Date.now(),
      dati_utente_completi: false, 
      error_log: [],
      storico_file: [],
      statistiche_utente: {
        fileCaricati: 0,
        tempoUtilizzoSecondi: 0,
        ultimoAccessoTimestamp: '',
      },
      statoInvito: 'Registrato',  
      inviti_emessi:{},
      aziende_invitate:[],
      collaboratori_invitati:[],
      clienti_invitati:[],
      file_cliente: [],
      avviso_setting: false,
      OptionsState: initialOptions,
    };
    
    await setDoc(docRef, { ...initialData }, { merge: true });

    return true;
  } catch (error:any){
    const errorMessage = getFriendlyFirebaseError(error.code);
    throw new Error(`${errorMessage}`);
  }
};


export async function registraUtente(email: string, password: string) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    await inviaEmailVerifica(user);
    
    return userCredential;

  } catch (error: any) {
    const errorMessage = getFriendlyFirebaseError(error.code);
    throw new Error(`${errorMessage}`);
  }
}



export const inviaInvito = async (emailInvitato: string, tipoInvitato: 'azienda' | 'collaboratore' | 'cliente'): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("Utente non autenticato.");

  try {
    const userRef = doc(db, "utenti", user.uid);
    const placeholderKey = emailInvitato.replace(/[.#$/[\]]/g, "_");
    await runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      const datiUtente = userSnap.data();

      // Dati del nuovo invito
      const nuovoInvito = {
        email: emailInvitato,
        tipo: tipoInvitato,
        stato: "in attesa",
      };

      // Controlla e inizializza la mappa
      if (!userSnap.exists() || !datiUtente?.inviti_emessi || typeof datiUtente.inviti_emessi !== 'object' || Array.isArray(datiUtente.inviti_emessi)) {
        transaction.set(userRef, { inviti_emessi: {} }, { merge: true }); // aggiunge senza sovrascrivere
      }

      // aggiungi l'invito nella mappa
      transaction.update(userRef, {
        [`inviti_emessi.${placeholderKey}`]: nuovoInvito,
      });
    });
  } catch (error) {
    console.error("Errore nell'invio dell'invito:", error);
    await addErrorLog("Errore Invio Invito", error);
    throw new Error("Impossibile inviare l'invito.<br/>Controlla la connessione e riprova.");
  }
};

export const salvaStatisticheUtilizzo = async (valore: number | string, chiave:'statistiche_utente.fileCaricati'|'statistiche_utente.tempoUtilizzoSecondi'|'statistiche_utente.ultimoAccessoTimestamp'): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;
  const docRef = doc(db, "utenti", user.uid);
  
  try {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      await updateDoc(docRef, {
        [chiave] : valore
      });
    }
  } catch (error) {
    console.error(`Errore aggiornamento ${chiave}:`, error);
    await addErrorLog(`Errore aggiornamento ${chiave}:`, error);
  }
};

 export const eliminaFile = async(nomiFile: string[]): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("Utente non autenticato.");

  const uid = user.uid;

  const storage = getStorage();
  const deletePromises = nomiFile.map(nomeFile => {
        const fileRef = ref(storage, `utenti/${uid}/file_cliente/${nomeFile}`);
        return deleteObject(fileRef);
    });
  try {
    await Promise.all(deletePromises); 
  } catch (error: any) {
    throw new Error(`Errore durante l'eliminazione: ${error.message}`);
  }
  try {
    const docRef = doc(db, "utenti", uid);
    const snap = await getDoc(docRef);

    if (!snap.exists()) return;

    const data = snap.data();
    const fileList = data.file_cliente || [];

    const nuovoArray = fileList.filter((file: any) => 
      !nomiFile.includes(file.nome)
    );

    await updateDoc(docRef, {
      file_cliente: nuovoArray
    });
  } catch (error:any) {
    throw new Error(`Errore durante l'eliminazione: ${error.message}`);
  }
}

export const caricaFileCliente = async (files: File[]): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("Utente non autenticato.");

  const docRef = doc(db, "utenti", user.uid);
  const docSnap = await getDoc(docRef);
  const rawData = docSnap.data();

  if (!docSnap.exists() || rawData?.tipo_account !== "cliente") {
    throw new Error("Solo gli account 'cliente' possono caricare file.");
  }

  for (const file of files) {
    // Carica file su Firebase Storage
    const filePath = `utenti/${user.uid}/file_cliente/${file.name}`;
    const fileRef = ref(storage, filePath);
    const metadata = {
      contentType: 'application/pdf', 
      contentDisposition: 'attachment' 
    };

    await uploadBytes(fileRef, file, metadata);

    // Recupera URL 
    const downloadURL = await getDownloadURL(fileRef);

    // salvare su database
    const nuovoFile = {
      nome: file.name,
      url: downloadURL,
      stato: "Inviato" as const,
      timestampCaricamento: Date.now()
    };

    // Salva il record 
    await updateDoc(docRef, {
      file_cliente: arrayUnion(nuovoFile)
    });
  }
};


export const aggiornaStatoFileCliente = async (uidCliente: string, nomeFile: string, idUtente:string , nuovoStato: 'Scaricato' | 'Elaborazione in corso' | 'Elaborato'): Promise<void> => {
    try {
        const docRef = doc(db, "utenti", uidCliente);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && docSnap.data().tipo_account === 'cliente') {
          const datiCliente = docSnap.data(); 
          //se lo stato è già Elaborato non lo modifichiamo.
          const fileDaAggiornare = datiCliente.file_cliente.find((file: FileCliente) => file.nome === nomeFile);
          if (fileDaAggiornare && fileDaAggiornare.stato === 'Elaborato') { return };

          const fileClienteAggiornato = (datiCliente.file_cliente || []).map((file:any) => {
            if (file.nome === nomeFile) {
              return { ...file, stato: nuovoStato, xmlElaboratiDa:idUtente };
            }
            return file;
          });

          await updateDoc(docRef, { file_cliente: fileClienteAggiornato });

        } else {
            throw new Error("Cliente non trovato o account non valido.");
        }
    } catch (error) {
        console.error("Errore aggiornamento stato file:", error);
        await addErrorLog("Errore Aggiornamento Stato File", error);
        throw new Error("Impossibile aggiornare lo stato del file.");
    }
};

export async function saveApiKey(uid: string, key:string): Promise<boolean>  {
  try{
    const docRef = doc(db, "raccolta", uid);
    await setDoc(docRef, {api_key: key});
    return true;
  } catch (error: any) {
      await addErrorLog("Errore Salvataggio Dati Utente", error);
      throw new Error("Si è verificato un errore nel salvataggio dei tuoi dati.", error);
  }
}

export async function completaProfilo(
    uid: string, 
    data: { tipoAccount: 'proprietario' | 'azienda' | 'collaboratore' | 'cliente' | string, pIva: string, ragioneSociale: string,  indirizzo: string, cap: string, comune: string, nazione: string, invitatoDaUID?:string, statoInvito?:string}
): Promise<boolean> {
    try {
        const docRef = doc(db, "utenti", uid);
        const updatePayload: any = {
          tipo_account: data.tipoAccount,
          dati_utente_completi: true ,
          ragione_sociale: data.ragioneSociale || '',
          partita_iva: data.pIva || '',
          indirizzo: data.indirizzo || '', 
          cap: data.cap || '', 
          comune: data.comune || '', 
          nazione: data.nazione || ''
        };
        
        const dataInvitato = data.invitatoDaUID
        if (dataInvitato){
          updatePayload.invitatoDaUID = dataInvitato
        }
        const statoInvito = data.statoInvito
        if (statoInvito){
          updatePayload.statoInvito = statoInvito
        }

        await updateDoc(docRef, updatePayload);

        return true;
    } catch (error: any) {
        await addErrorLog("Errore Salvataggio Dati Utente", error);
        throw new Error("Si è verificato un errore nel salvataggio dei tuoi dati.", error);
    }
}

export const saveHash = async (hash: string) => {
  const user = auth.currentUser;
  if (user) {
    const docRef = doc(db, "utenti", user.uid); 
    const docSnap = await getDoc(docRef);

    let storico: Record<string, number>[] = [];
    if (docSnap.exists()) {
      const data = docSnap.data();
      storico = data.storico_file || [];
    }

    const esiste = storico.some(entry => Object.keys(entry)[0] === hash);
    if (!esiste) {
      storico.push({ [hash]: Date.now() });

      await updateDoc(docRef, {
        storico_file: storico,
      });
    }
  }
};

// dopo un anno vengono cancellati gli hashes.
export const cleanOldFileHashes = async (uid: string) => {
  const docRef = doc(db, "utenti", uid);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return;

  const data = docSnap.data();
  const storico: Record<string, number>[] = data.storico_file || [];
  const oneYearAgo = Date.now() - 365 * 24 * 60 * 60 * 1000;

  const storicoPulito = storico.filter(entry => {
    const timestamp = Object.values(entry)[0];
    return typeof timestamp === "number" && timestamp >= oneYearAgo;
  });

  if (storicoPulito.length < storico.length) {
    await updateDoc(docRef, {
      storico_file: storicoPulito,
    });
  }
};

// gli errori vengono cancellati dopo 24 ore
const cleanOldLogs = async (uid: string) => {
  const docRef = doc(db, "utenti", uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const data = docSnap.data();
    const now = new Date().getTime();
    const filteredLogs = (data.error_log || []).filter((log: string) => {
      const dateLine = log.split("\n")[0];
      const logDate = new Date(dateLine).getTime();
      return now - logDate < 24 * 60 * 60 * 1000; // 24 ore in ms
    });

    await updateDoc(docRef, { error_log: filteredLogs });
  }
};

// CRUD anagrafica per le fatture
export const addCliente = async (cliente: Cliente) => {
  const user = auth.currentUser;
  if (!user) return;
  const docRef = doc(db, "utenti", user.uid);
  await updateDoc(docRef, {
    anagrafica_clienti: arrayUnion(cliente)
  });
};

export const updateCliente = async (cliente: Cliente) => {
  const user = auth.currentUser;
  if (!user) return;
  const docRef = doc(db, "utenti", user.uid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    const updatedClienti = data.anagrafica_clienti.map((c: Cliente) =>
      c.partita_iva === cliente.partita_iva ? cliente : c
    );
    await updateDoc(docRef, { anagrafica_clienti: updatedClienti });
  }
};

export const deleteCliente = async (cliente: Cliente) => {
  const user = auth.currentUser;
  if (!user) return;
  const docRef = doc(db, "utenti", user.uid);
  await updateDoc(docRef, {
    anagrafica_clienti: arrayRemove(cliente)
  });
};


// salva messaggio errore
export const addErrorLog = async (errorName: string, errorMessage: string | unknown) => {
  const user = auth.currentUser;
  if (!user) return;
  const docRef = doc(db, "utenti", user.uid);
  const date = new Date().toISOString();
  const errorString = `${date}\n\n${errorName}\n${errorMessage}`;
  await updateDoc(docRef, { error_log: arrayUnion(errorString) });
};


//login e logout
export async function loginUtente(email: string, password: string): Promise<boolean> {
  try {
    await signInWithEmailAndPassword(auth, email, password);
    return true;
  } catch (error: any) {
    const errorMessage = getFriendlyFirebaseError(error.code);
    throw new Error(`${errorMessage}`);
  }
}

export async function logoutUtente() {
  try {
    await signOut(auth);
    return true;
  } catch (error: any) {
    const msg = getFriendlyFirebaseError(error.code || "generic");
    throw new Error(`${msg}`);
  }
}

//data formattata
const formatErrorDate = (logString: string): string => {
    const parts = logString.split('\n');
    if (parts.length < 1) return logString; 

    const isoDateString = parts[0]; 
    
    try {
        const date = new Date(isoDateString);
        
        const localDate = date.toLocaleString(
            navigator.language, 
            {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false, 
            }
        );
        parts[0] = localDate;
        
        return parts.join('\n');

    } catch (e) {
        return logString;
    }
};

// Stato iniziale
const initialDataState: FirestoreData = {
    uid: '',
    email: '',
    userPhotoURL:'',
    data_registrazione:0,
    dati_utente_completi: false,
    error_log: [],
    statistiche_utente: { fileCaricati: 0, tempoUtilizzoSecondi: 0, ultimoAccessoTimestamp: '' }, 
    storico_file: [],
    tipo_account: '',
    
    // Campi specifici
    ragione_sociale: '',
    partita_iva: '',
    indirizzo: '',
    cap: '',
    comune: '',
    nazione: '',
    anagrafica_clienti: [],
    file_cliente: [],
    
    // Campi opzionali 
    invitatoDaUID: undefined, 
    statoInvito: undefined,
    inviti_emessi: {},
    aziende_invitate:[],
    collaboratori_invitati:[],
    clienti_invitati:[],
    avviso_setting:false,
    OptionsState: initialOptions,
};

type InvitedUsersMap = Record<string, UtenteDB>;

//legge in tempo reale statistiche_utente.fileCaricati
export const useFileCaricati = () => {
    const [fileCaricati, setFileCaricati] = useState<number | null>(null);
    const [tempoUtilizzo, setTempoUtilizzo] = useState<number | null>(null);
    const [dataIsLoad, setDataIsLoad] = useState(false);

    useEffect(() => {
        let unsubscribeSnapshot: (() => void) | undefined;
        
        // Listener per l'autenticazione
        const unsubscribeAuth = onAuthStateChanged(auth, (user: User | null) => {
            if (user) {
                const uid = user.uid;
                const docRef = doc(db, "utenti", uid);
                
                // Listener per i dati di Firestore
                unsubscribeSnapshot = onSnapshot(docRef, (docSnap) => {
                    if (docSnap.exists()) {
                        const rawData = docSnap.data() as UtenteDB;
                        
                        // Aggiorna solo il contatore fileCaricati
                        const count = rawData.statistiche_utente?.fileCaricati;
                        setFileCaricati(count);
                        const time = rawData.statistiche_utente?.tempoUtilizzoSecondi;
                        setTempoUtilizzo(time);
                        setDataIsLoad(true);
                    } else {
                        setFileCaricati(0);
                        setTempoUtilizzo(0);
                        setDataIsLoad(true);
                    }
                }, (error) => {
                    console.error("Errore onSnapshot:", error);
                });

            } else {
                setFileCaricati(0);
                setTempoUtilizzo(0);
                setDataIsLoad(true);
            }
        });

        // Funzione di cleanup
        return () => {
            if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
            }
            unsubscribeAuth();
        };
    }, [auth]);
    return { fileCaricati , tempoUtilizzo, dataIsLoad};
};

//funzione Admin/Azienda condivisione di un cliente 
export const condividiCliente = async (
  clienteUID: string,
  collaboratoreUID: string
) => {
  const docRef = doc(db, "utenti", collaboratoreUID);

  await updateDoc(docRef, {
    clienti_invitati: arrayUnion(clienteUID)
  });
};

//funzione per rimuovere la condivisione di un cliente
export const rimuoviCondivisioneCliente = async (
  clienteUID: string,
  collaboratoreUID: string
) => {
  const docRef = doc(db, "utenti", collaboratoreUID);

  await updateDoc(docRef, {
    clienti_invitati: arrayRemove(clienteUID)
  });
};


// modifica un campo del database
export const ChangeOneVariable = async(key:string, value:any) => {
  const userDocRef = doc(db, "utenti", String(auth.currentUser?.uid));
  try {
    await updateDoc(userDocRef, {
      [key]: value
    });
  } catch (error) {
    console.error("Errore durante l'aggiornamento:", error);
  }
}

// Estrae i dati in base all'id salvato
export const useDettagliUtentiInvitati = (arrayUID: string[]): { invitedUsers: InvitedUsersMap } => {
    const [invitedUsers, setInvitedUsers] = useState<InvitedUsersMap>({}); 
    const unsubscribeRefs = useRef<(() => void)[]>([]);
    const unsubscribeRTDBRefs = useRef<(() => void)[]>([]); 

    useEffect(() => {
        // Pulizia Vecchi Listener
        unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
        unsubscribeRefs.current = [];
        unsubscribeRTDBRefs.current.forEach(unsubscribe => unsubscribe()); 
        unsubscribeRTDBRefs.current = [];

        if (!arrayUID || arrayUID.length === 0) {
            setInvitedUsers({});
            return;
        }

        const newUnsubscribes: (() => void)[] = [];
        const newUnsubscribesRTDB: (() => void)[] = [];
        
        arrayUID.forEach(uid => {
            const firestoreDocRef = doc(db, 'utenti', uid);
            
            // Listener FIRESTORE 
            const unsubscribeFirestore = onSnapshot(firestoreDocRef, (docSnap) => {
                if (docSnap.exists()) {
                    const rawData = docSnap.data() as UtenteDB;
                    rawData.uid = uid;

                    // unisci i nuovi dati Firestore con lo stato di presenza esistente
                    setInvitedUsers((prevUsers) => {
                        const existingPresenceStatus = prevUsers[uid]?.statistiche_utente?.ultimoAccessoTimestamp || '';
                        
                        const mergedData = {
                            ...rawData,
                            statistiche_utente: {
                                ...rawData.statistiche_utente,
                                ultimoAccessoTimestamp: existingPresenceStatus, 
                            }
                        };
                        
                        // non fare nulla se i dati Firestore sono gli stessi
                        if (prevUsers[uid] && JSON.stringify({...prevUsers[uid], statistiche_utente: rawData.statistiche_utente}) === JSON.stringify(rawData)) {
                            return prevUsers;
                        }

                        return {
                            ...prevUsers,
                            [uid]: mergedData,
                        };
                    });
                } else {
                    // Logica per documenti non esistenti
                    setInvitedUsers((prevUsers) => {
                        const newUsers = { ...prevUsers };
                        delete newUsers[uid];
                        return newUsers;
                    });
                }
            });
            
            //Listener RTDB
            const rtdbRefForPresence = databaseRef(realtimeDb, `presenza/${uid}`);
            
            const unsubscribeRTDB = onValue(rtdbRefForPresence, (snapshot) => {
                if (snapshot.exists()) {
                    const rtdbData = snapshot.val(); 
                    const presenceStatus = rtdbData?.ultimoAccessoTimestamp || null;
                    
                    if (presenceStatus) {
                        setInvitedUsers((prevUsers) => {
                            const userToUpdate = prevUsers[uid];
                            if (!userToUpdate) return prevUsers; // Utente non ancora caricato da Firestore
                            
                            // Aggiorna solo il campo ultimoAccessoTimestamp
                            return {
                                ...prevUsers,
                                [uid]: {
                                    ...userToUpdate,
                                    statistiche_utente: {
                                        ...userToUpdate.statistiche_utente,
                                        ultimoAccessoTimestamp: presenceStatus, 
                                    }
                                }
                            };
                        });
                    }
                }
            });

            newUnsubscribes.push(unsubscribeFirestore);
            newUnsubscribesRTDB.push(unsubscribeRTDB); 
        });

        unsubscribeRefs.current = newUnsubscribes;
        unsubscribeRTDBRefs.current = newUnsubscribesRTDB;

        return () => {
          newUnsubscribes.forEach(unsubscribe => unsubscribe());
          newUnsubscribesRTDB.forEach(unsubscribe => unsubscribe());
        };
        
    }, [arrayUID]); 

    return { invitedUsers };
};

export const useFirestoreData = () => {
  const [data, setData] = useState<FirestoreData>(initialDataState); 
  const [errorLog, setErrorLog] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [isDataLoad, setIsDataLoad] = useState(false);

  const { invitedUsers: aziendeMap } = useDettagliUtentiInvitati(data.aziende_invitate || []);
  const { invitedUsers: collaboratoriMap } = useDettagliUtentiInvitati(data.collaboratori_invitati || []);
  const { invitedUsers: clientiMap } = useDettagliUtentiInvitati(data.clienti_invitati || []);

  // Estraiamo gli uid
  const uidsElaboratori = useMemo(() => {
    const uids = new Set<string>();
    if (data.file_cliente) {
      data.file_cliente.forEach(file => {
        if (file.xmlElaboratiDa) {
          uids.add(file.xmlElaboratiDa);
        }
      });
    }
    return Array.from(uids);
  }, [data.file_cliente]);

  //chi ha eleborato il file
  const { invitedUsers: elaboratoriMap } = useDettagliUtentiInvitati(uidsElaboratori || []);
  
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setUserId(uid);
        cleanOldFileHashes(user.uid);

        const docRef = doc(db, "utenti", uid);
        
        const unsubscribeSnapshot = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const rawData = docSnap.data() as UtenteDB; 
            
            const formattedErrorLog = (rawData.error_log || []).map(formatErrorDate);
            setErrorLog(formattedErrorLog || []);
            
            cleanOldLogs(uid); 
            
            setData((prevData) => ({
              ...prevData, 
              ...rawData, 
              email: user.email || '', 
              uid: uid,
            } as FirestoreData)); 

          } else {
            // se non esistente
            setData({
              ...initialDataState,
              uid: uid,
              email: user.email || '',
              dati_utente_completi: false,
            });
          }
          setIsDataLoad(true);
        });

        return () => unsubscribeSnapshot(); 

      }
    });

    return () => unsubscribeAuth();
  }, []);

  
  // Funzione helper l'array di UID in un array di oggetti UtenteDB
  const getDetailedList = (uids: string[] | undefined, userMap: InvitedUsersMap): (UtenteDB & Partial<DatiAnagraficaAzienda>)[] => {
    if (!uids || uids.length === 0) return [];
        
    return uids
      .map(uid => userMap[uid])
      .filter((user): user is UtenteDB & Partial<DatiAnagraficaAzienda> => !!user); // Filtra e tipizza
  };

  // Arricchiamo l'array fileCliente 
  const fileClienteArricchito: FileClienteArricchito[] = useMemo(() => {
    return (data.file_cliente || []).map(file => {
      const elaboratoreUid = file.xmlElaboratiDa;
      const elaboratore = elaboratoriMap[elaboratoreUid];
      
      if (elaboratore) {
        return {
          ...file,
          xmlElaboratiDa: {
            uid: elaboratoreUid,
            email: elaboratore.email,
            userPhotoURL: elaboratore.userPhotoURL,
          },
        } as FileClienteArricchito;
      }
      
      return file as FileClienteArricchito; 
    });
  }, [data.file_cliente, elaboratoriMap]);

  const finalCollaboratoriMap = getDetailedList(data.collaboratori_invitati || [], collaboratoriMap);

  // Mappa clienti dei collaboratori
  const clientiCollaboratoriMap = useMemo(() => {
    if (!finalCollaboratoriMap) return {};

    const map: Record<string, string[]> = {};

    //const clientiPropri = new Set(data.clienti_invitati || []);

    finalCollaboratoriMap.forEach(coll => {
      const clienti = (coll.clienti_invitati || []).filter(
        uid => uid //!clientiPropri.has(uid)  // esclusi gli accont clienti che già abbiamo
      );

      if (clienti.length > 0) {
        map[coll.uid] = clienti;
      }
    });

    return map;
  }, [finalCollaboratoriMap, data.clienti_invitati]);

  const prevAllClientiCollaboratoriUIDsRef = useRef<string[]>([]);
  const allClientiCollaboratoriUIDs = useMemo(() => {
    const set = new Set<string>();

    Object.values(clientiCollaboratoriMap).forEach(listaClienti => {
      listaClienti.forEach(uid => set.add(uid));
    });
    const newUids = Array.from(set);
    if (prevAllClientiCollaboratoriUIDsRef.current.length === newUids.length) {
      // Se la lunghezza è la stessa, restituisci il vecchio array stabile
      return prevAllClientiCollaboratoriUIDsRef.current;
    }
    prevAllClientiCollaboratoriUIDsRef.current = newUids;
    return newUids;
  }, [clientiCollaboratoriMap]);


  const { invitedUsers: dettagliClientiCollaboratori  } = useDettagliUtentiInvitati(allClientiCollaboratoriUIDs);

  const clientiCollaboratoriDettagliati = useMemo(() => {
    const map: Record<string, { uid: string, email: string, userPhotoURL: string }[]> = {};

    Object.entries(clientiCollaboratoriMap).forEach(([collUid, listaClienti]) => {
      map[collUid] = listaClienti
        .map(uid => dettagliClientiCollaboratori[uid])
        .filter((user): user is UtenteDB => !!user) 
        .map(user => ({
          uid: user.uid,
          email: user.email,
          userPhotoURL: user.userPhotoURL,
          // Aggiungi qui eventuali altri campi informativi essenziali
        }));
    });
    return map;
  }, [clientiCollaboratoriMap, dettagliClientiCollaboratori]);

  return { 
    anagraficaClienti: data.anagrafica_clienti,
    errorLog, 
    storicoFile: data.storico_file,
    datiCompleti: data.dati_utente_completi,
    tipoAccount: data.tipo_account, 
    email:data.email,
    userPhotoURL:data.userPhotoURL,
    ragioneSociale: data.ragione_sociale,
    partitaIva: data.partita_iva, 
    indirizzo: data.indirizzo,
    cap: data.cap,
    comune: data.comune,
    nazione: data.nazione,
    userId,
    statisticheUtente: data.statistiche_utente,
    fileCliente: fileClienteArricchito, 
    invitatoDaUID: data.invitatoDaUID,
    statoInvito: data.statoInvito,
    aziende_invitate: getDetailedList(data.aziende_invitate || [], aziendeMap),
    collaboratori_invitati: finalCollaboratoriMap,
    clienti_invitati: getDetailedList(data.clienti_invitati || [], clientiMap),
    inviti_emessi: data.inviti_emessi,
    avvisoSetting: data.avviso_setting,
    OptionsState: data.OptionsState,
    clientiCollaboratori: clientiCollaboratoriDettagliati,
    isDataLoad
  };
};

export default useFirestoreData;


// Funzione per recuperare i file e gli url
export const useClientFiles = (uid: string) => {
  // due categorie di file
  const [files, setFiles] = useState<CategorizedFiles>({
    Inviato: [],
    Elaborato: [],
  });
  const [isLoadingCF, setIsLoadingCF] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset degli stati
    setFiles({ Inviato: [], Elaborato: [] });
    setIsLoadingCF(true);
    setError(null);

    if (!uid) {
      setIsLoadingCF(false);
      return;
    }

    const fetchFilesFromDB = async () => {
      try {
        const userDocRef = doc(db, 'utenti', uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
          console.log("Nessun documento utente trovato per l'UID:", uid);
          setIsLoadingCF(false);
          return;
        }

        const userData = userDocSnap.data() as UtenteDB;
        const clientFiles: FileCliente[] = (userData.file_cliente || []);
        
        // Estrai UID unici degli elaboratori
        const elaboratoriUIDs = Array.from(new Set(
            clientFiles
                .map(file => file.xmlElaboratiDa)
                .filter((id): id is string => !!id)
        ));

        // Recupera i dettagli degli elaboratori in parallelo (una tantum)
        const elaboratoriMap: Record<string, { uid: string; email: string; userPhotoURL?: string }> = {};
        if (elaboratoriUIDs.length > 0) {
            const promises = elaboratoriUIDs.map(async (elaboratoreUid) => {
                const elaboratoreDocRef = doc(db, 'utenti', elaboratoreUid);
                const elaboratoreDocSnap = await getDoc(elaboratoreDocRef);
                
                if (elaboratoreDocSnap.exists()) {
                    const elaboratoreData = elaboratoreDocSnap.data() as UtenteDB;
                    elaboratoriMap[elaboratoreUid] = {
                        uid: elaboratoreUid,
                        email: elaboratoreData.email || 'Email non disponibile',
                        userPhotoURL: elaboratoreData.userPhotoURL,
                    };
                }
            });
            await Promise.all(promises);
        }

        // Arricchiamo l'array fileCliente 
        const fileClienteArricchito: FileClienteArricchito[] = clientFiles.map(file => {
            const elaboratoreUid = file.xmlElaboratiDa;
            const elaboratoreDettagli = elaboratoriMap[elaboratoreUid as string];

            if (elaboratoreUid && elaboratoreDettagli) {
                return {
                    ...file,
                    xmlElaboratiDa: elaboratoreDettagli,
                } as FileClienteArricchito;
            }
            
            return file as FileClienteArricchito; 
        });


        const categorized: CategorizedFiles = {
          Inviato: [],
          Elaborato: [],
        };

        // Categorizzazione dei file arricchiti
        fileClienteArricchito.forEach(file => {
          if (file.stato === 'Inviato' || file.stato ==='Scaricato') {
            categorized.Inviato.push(file);
          } else if (file.stato === 'Elaborato' || file.stato ==='Elaborazione in corso') { 
            categorized.Elaborato.push(file);
          }
        });

        setFiles(categorized);

      } catch (err: any) {
        console.error('Errore nel recupero dei file dal database:', err);
        setError(err.message || 'Errore sconosciuto nel recupero dei file dal DB.');
      } finally {
        setIsLoadingCF(false);
      }
    };

    fetchFilesFromDB();

    // Cleanup
    return () => {}; 
  }, [uid]); 

  return { files, isLoadingCF, error };
};



//Cancella Account 

export const verifyPasswordBeforeDelete = async (password: string): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error("Nessun utente autenticato.");
  }
  try {
    const credential = EmailAuthProvider.credential(user.email, password);

    await reauthenticateWithCredential(user, credential); 
    return true;
  } catch (error: any) {
    const msg = getFriendlyFirebaseError(error.code || "generic");
    await addErrorLog("Password non valida: ",`${msg}`);
    throw new Error(`${msg}`);
  };
};


export const deleteEmailPasswordAccount = async () => {
  const user = auth.currentUser;
  const nodoRef = databaseRef(realtimeDb, `presenza/${user?.uid}`);
  if (!user) {
    throw new Error("Nessun utente autenticato.");
  }
  try {
    await deleteDoc(doc(db, "utenti", user.uid));
    await remove(nodoRef);
    await deleteUser(user);
    return true
  } catch (error: any) {
    const msg = getFriendlyFirebaseError(error.code || "generic");
    await addErrorLog("Errore eliminazione account (email/password)", error);
    throw new Error(`${msg}`);
  }
};

export const deleteGoogleAccount = async () => {
  const user = auth.currentUser;
  const nodoRef = databaseRef(realtimeDb, `presenza/${user?.uid}`);
  if (!user) {
    throw new Error("Nessun utente autenticato.");
  }
  try {
    await reauthenticateWithPopup(user, googleProvider); 
    await deleteDoc(doc(db, "utenti", user.uid));
    await remove(nodoRef);
    await deleteUser(user);
    return true
  } catch (error: any) {
    const msg = getFriendlyFirebaseError(error.code || "generic");
    await addErrorLog("Errore durante la cancellazione account Google: ",`${msg}`);
    throw new Error(`${msg}`);
  }
};
